// Greedy columns for diagnosis only — do not merge with verticalBlockingFix's seam union-find.
import { itemsWithLayout, slabBlockKeyOf } from "../verticalBlockingFix";
import { GeneratedLayout, StudioDraft, StudioItem, StudioRunGeometry } from "../studioTypes";

export interface DiagnosedColumn {
  run: number;
  /** Block keys, left to right within the column's x-window. */
  members: readonly string[];
  shelves: readonly number[];
  x0: number;
  x1: number;
  fullHeight: boolean;
}

export interface ColumnDiagnosis {
  cols: readonly DiagnosedColumn[];
  /** Columns that do not cover their run's height — the stranding census. */
  partial: number;
  /** Block keys living in a partial column. */
  stranded: readonly string[];
  /** Total spread of each column's own left edge across its shelves, cm. */
  colRagged: number;
}

interface BlockBox {
  key: string;
  x0: number;
  x1: number;
  shelves: Set<number>;
  startPerShelf: Map<number, number>;
}

export const diagnoseColumns = (
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  options?: {
    /** Override block identity; the rehome pipeline diagnoses at the moved level's granularity. */
    blockKeyOf?: (item: StudioItem) => string;
  },
): ColumnDiagnosis => {
  const keyOf = options?.blockKeyOf ?? ((item: StudioItem): string => slabBlockKeyOf(draft, item));
  const projected = itemsWithLayout(items, layout);

  const cols: DiagnosedColumn[] = [];
  let colRagged = 0;
  for (const run of runs) {
    const boxes = new Map<string, BlockBox>();
    for (const item of projected) {
      const key = keyOf(item);
      for (const placement of item.placements) {
        if (placement.run !== run.run) continue;
        let box = boxes.get(key);
        if (!box) {
          box = { key, x0: Infinity, x1: -Infinity, shelves: new Set(), startPerShelf: new Map() };
          boxes.set(key, box);
        }
        box.x0 = Math.min(box.x0, placement.xCm);
        box.x1 = Math.max(box.x1, placement.xCm + placement.widthCm);
        box.shelves.add(placement.shelf);
        const start = box.startPerShelf.get(placement.shelf);
        if (start === undefined || placement.xCm < start) {
          box.startPerShelf.set(placement.shelf, placement.xCm);
        }
      }
    }
    const runShelfCount = run.shelves.length;
    const ordered = [...boxes.values()].sort((a, b) => (a.x0 + a.x1) / 2 - (b.x0 + b.x1) / 2);

    interface Accumulating {
      members: BlockBox[];
      covered: Set<number>;
    }
    const finished: Accumulating[] = [];
    let current: Accumulating | null = null;
    for (const box of ordered) {
      if (box.shelves.size === runShelfCount) {
        // A column on its own; anything half-accumulated stays partial, never swallowed by it.
        if (current) {
          finished.push(current);
          current = null;
        }
        finished.push({ members: [box], covered: new Set(box.shelves) });
        continue;
      }
      if (!current) {
        current = { members: [box], covered: new Set(box.shelves) };
        continue;
      }
      const clash = [...box.shelves].some((shelf) => current!.covered.has(shelf));
      if (clash) {
        finished.push(current);
        current = { members: [box], covered: new Set(box.shelves) };
        continue;
      }
      current.members.push(box);
      for (const shelf of box.shelves) current.covered.add(shelf);
      if (current.covered.size === runShelfCount) {
        finished.push(current);
        current = null;
      }
    }
    if (current) finished.push(current);

    for (const column of finished) {
      const shelves = [...column.covered].sort((a, b) => a - b);
      const col: DiagnosedColumn = {
        run: run.run,
        members: column.members.map((box) => box.key),
        shelves,
        x0: Math.min(...column.members.map((box) => box.x0)),
        x1: Math.max(...column.members.map((box) => box.x1)),
        fullHeight: shelves.length === runShelfCount,
      };
      cols.push(col);
      const starts: number[] = [];
      for (const shelf of shelves) {
        const perShelf = column.members
          .map((box) => box.startPerShelf.get(shelf))
          .filter((value): value is number => value !== undefined);
        if (perShelf.length > 0) starts.push(Math.min(...perShelf));
      }
      if (starts.length > 1) colRagged += Math.max(...starts) - Math.min(...starts);
    }
  }

  const partialCols = cols.filter((col) => !col.fullHeight);
  return {
    cols,
    partial: partialCols.length,
    stranded: partialCols.flatMap((col) => col.members),
    colRagged: +colRagged.toFixed(1),
  };
};
